import React, { useState, useEffect } from 'react';
import Login from './components/Login';
import CheckIn from './components/CheckIn';
import CheckOut from './components/CheckOut';
import PatrolApp from './components/PatrolApp';
import Reports from './components/Reports';

function App() {
  const [currentUser, setCurrentUser] = useState(null);
  const [currentPage, setCurrentPage] = useState('checkin');
  const [isLoading, setIsLoading] = useState(true);

  // Check for saved login on app load
  useEffect(() => {
    const savedUser = localStorage.getItem('securityUser');
    const savedPage = localStorage.getItem('securityCurrentPage');
    
    if (savedUser) {
      setCurrentUser(savedUser);
    }
    if (savedPage) {
      setCurrentPage(savedPage);
    }
    
    setIsLoading(false);
  }, []);

  const handleLogin = (username) => {
    setCurrentUser(username);
    setCurrentPage('checkin');
    // Save to localStorage
    localStorage.setItem('securityUser', username);
    localStorage.setItem('securityCurrentPage', 'checkin');
  };

  const handleLogout = () => {
    setCurrentUser(null);
    setCurrentPage('checkin');
    // Clear from localStorage
    localStorage.removeItem('securityUser');
    localStorage.removeItem('securityCurrentPage');
  };

  const changePage = (page) => {
    setCurrentPage(page);
    // Save current page to localStorage
    localStorage.setItem('securityCurrentPage', page);
  };

  // Show loading while checking saved login
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="text-2xl mb-4">🔐</div>
          <p className="text-gray-600">Loading Security System...</p>
        </div>
      </div>
    );
  }

  // If not logged in, show login screen
  if (!currentUser) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Security Management System</h1>
              <p className="text-sm text-gray-600">
                Logged in as: <span className="font-medium text-blue-600">{currentUser}</span> | 
                Current page: <span className="font-medium text-green-600">{currentPage}</span>
              </p>
            </div>
            <button
              onClick={handleLogout}
              className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 font-medium"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-white shadow-sm">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex space-x-8">
            <button
              onClick={() => changePage('checkin')}
              className={`inline-flex items-center px-6 py-4 text-lg font-medium border-b-2 transition-colors ${
                currentPage === 'checkin'
                  ? 'text-blue-600 border-blue-500'
                  : 'text-gray-500 hover:text-gray-700 border-transparent hover:border-gray-300'
              }`}
            >
              Check-In
            </button>
            <button
              onClick={() => changePage('checkout')}
              className={`inline-flex items-center px-6 py-4 text-lg font-medium border-b-2 transition-colors ${
                currentPage === 'checkout'
                  ? 'text-blue-600 border-blue-500'
                  : 'text-gray-500 hover:text-gray-700 border-transparent hover:border-gray-300'
              }`}
            >
              Check-Out
            </button>
            <button
              onClick={() => changePage('patrol')}
              className={`inline-flex items-center px-6 py-4 text-lg font-medium border-b-2 transition-colors ${
                currentPage === 'patrol'
                  ? 'text-blue-600 border-blue-500'
                  : 'text-gray-500 hover:text-gray-700 border-transparent hover:border-gray-300'
              }`}
            >
              Patrol
            </button>
            <button
              onClick={() => changePage('reports')}
              className={`inline-flex items-center px-6 py-4 text-lg font-medium border-b-2 transition-colors ${
                currentPage === 'reports'
                  ? 'text-blue-600 border-blue-500'
                  : 'text-gray-500 hover:text-gray-700 border-transparent hover:border-gray-300'
              }`}
            >
              Reports
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="py-6">
        {currentPage === 'checkin' && <CheckIn currentUser={currentUser} />}
        {currentPage === 'checkout' && <CheckOut />}
        {currentPage === 'patrol' && <PatrolApp />}
        {currentPage === 'reports' && <Reports />}
      </main>
    </div>
  );
}

export default App;
